import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Modal, Form, Input, Select, message, Tag, Space, Popconfirm, Typography, Result } from 'antd';
import { PlusOutlined, SyncOutlined, EditOutlined, DeleteOutlined, ShopOutlined, ShoppingOutlined, AmazonOutlined, AppstoreOutlined, GlobalOutlined, CrownOutlined } from '@ant-design/icons';
import '../admin.css';
import { hasFeatureAccess, getUpgradeMessage } from '../utils/planRestrictions';
import UpgradeModal from '../components/UpgradeModal';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title, Text } = Typography;
const { Option } = Select;

// Platform configurations with icons/colors
const PLATFORMS = [
  { value: 'Shopify', label: 'Shopify', icon: <ShopOutlined />, color: '#96bf48' },
  { value: 'eBay', label: 'eBay', icon: <ShoppingOutlined />, color: '#e53238' },
  { value: 'WooCommerce', label: 'WooCommerce', icon: <ShoppingOutlined />, color: '#96588a' },
  { value: 'Amazon', label: 'Amazon', icon: <AmazonOutlined />, color: '#ff9900' },
  { value: 'Etsy', label: 'Etsy', icon: <ShoppingOutlined />, color: '#f1641e' },
  { value: 'BigCommerce', label: 'BigCommerce', icon: <ShopOutlined />, color: '#1d4d8f' },
  { value: 'Magento', label: 'Magento', icon: <AppstoreOutlined />, color: '#ee672f' },
  { value: 'Other', label: 'Other', icon: <GlobalOutlined />, color: '#666666' }
];

const APIKeys = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchCurrentPlan = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/current-plan`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentPlan(data);
      } else {
        setCurrentPlan(null);
      }
    } catch (err) {
      console.error('Error fetching plan:', err);
      setCurrentPlan(null);
    }
  }, []);

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/my/api-integrations`, {
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (res.ok) {
        setIntegrations(data);
      } else {
        message.error(data.error || 'Failed to load integrations');
      }
    } catch (err) {
      message.error('Error loading integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
    fetchCurrentPlan();
  }, [loadIntegrations, fetchCurrentPlan]);

  const handleAdd = () => {
    // Check if user has access to API features
    if (!hasFeatureAccess(currentPlan, 'api_access')) {
      setUpgradeModalVisible(true);
      return;
    }
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    // Check if user has access to API features
    if (!hasFeatureAccess(currentPlan, 'api_access')) {
      setUpgradeModalVisible(true);
      return;
    }
    setEditingId(record.id);
    form.setFieldsValue({
      platform: record.platform,
      label: record.label,
      store_url: record.store_url,
      // Don't populate api_key and api_secret for security
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const url = editingId 
        ? `${apiBase}/my/api-integrations/${editingId}`
        : `${apiBase}/my/api-integrations`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(values)
      });

      const data = await res.json();
      if (res.ok) {
        message.success(editingId ? 'Integration updated' : 'Integration added');
        setModalVisible(false);
        loadIntegrations();
      } else {
        message.error(data.error || 'Operation failed');
      }
    } catch (err) {
      message.error('Validation failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${apiBase}/my/api-integrations/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (res.ok) {
        message.success('Integration deleted');
        loadIntegrations();
      } else {
        message.error(data.error || 'Delete failed');
      }
    } catch (err) {
      message.error('Error deleting integration');
    }
  };

  const handleSync = async (id, platform) => {
    // Check if user has access to API features
    if (!hasFeatureAccess(currentPlan, 'api_access')) {
      setUpgradeModalVisible(true);
      return;
    }
    try {
      const res = await fetch(`${apiBase}/my/api-integrations/${id}/sync`, {
        method: 'POST',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (res.ok) {
        message.success(`${platform} sync initiated`);
        loadIntegrations();
      } else {
        message.error(data.error || 'Sync failed');
      }
    } catch (err) {
      message.error('Error syncing');
    }
  };

  const columns = [
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (text) => {
        const platform = PLATFORMS.find(p => p.value === text);
        return (
          <Space>
            <span style={{ color: platform?.color || '#666', fontSize: 18 }}>
              {platform?.icon || <ShopOutlined />}
            </span>
            <Text strong>{text}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (text) => text || '—'
    },
    {
      title: 'Store URL',
      dataIndex: 'store_url',
      key: 'store_url',
      render: (text) => text ? <a href={text} target="_blank" rel="noopener noreferrer">{text}</a> : '—'
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Last Synced',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      render: (date) => date ? new Date(date).toLocaleString() : 'Never'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            icon={<SyncOutlined />} 
            onClick={() => handleSync(record.id, record.platform)}
          >
            Sync
          </Button>
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this integration?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Check if user has access to API Keys
  const hasAPIAccess = hasFeatureAccess(currentPlan, 'api_access');
  const upgradeMessage = getUpgradeMessage('api_access');

  // If user doesn't have access, show upgrade prompt
  if (!hasAPIAccess && currentPlan) {
    return (
      <div className="abo-content">
        <Title level={2}>API Integrations</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Connect your Shopify, eBay, or other e-commerce platforms to sync metrics and analytics.
        </Text>

        <Card>
          <Result
            icon={<CrownOutlined style={{ color: '#722ed1' }} />}
            title={upgradeMessage.title}
            subTitle={upgradeMessage.description}
            extra={[
              <Button
                key="upgrade"
                type="primary"
                size="large"
                onClick={() => setUpgradeModalVisible(true)}
                style={{ background: '#722ed1', borderColor: '#722ed1' }}
              >
                Upgrade to Enterprise
              </Button>
            ]}
          />
        </Card>

        <UpgradeModal
          visible={upgradeModalVisible}
          onClose={() => setUpgradeModalVisible(false)}
          limitType="api_access"
          currentPlan={currentPlan?.plan_name}
        />
      </div>
    );
  }

  return (
    <div className="abo-content">
      <Title level={2}>API Integrations</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Connect your Shopify, eBay, or other e-commerce platforms to sync metrics and analytics.
      </Text>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Integration
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={integrations}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Integration' : 'Add Integration'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingId ? 'Update' : 'Add'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="platform"
            label="Platform"
            rules={[{ required: true, message: 'Please select a platform' }]}
          >
            <Select 
              placeholder="Select platform"
              optionLabelProp="label"
            >
              {PLATFORMS.map(platform => (
                <Option 
                  key={platform.value} 
                  value={platform.value}
                  label={
                    <Space>
                      <span style={{ color: platform.color }}>{platform.icon}</span>
                      <span>{platform.label}</span>
                    </Space>
                  }
                >
                  <Space>
                    <span style={{ color: platform.color, fontSize: 16 }}>{platform.icon}</span>
                    <span>{platform.label}</span>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="label"
            label="Label (Optional)"
          >
            <Input placeholder="e.g., My Main Store" />
          </Form.Item>

          <Form.Item
            name="store_url"
            label="Store URL (Optional)"
          >
            <Input placeholder="https://mystore.myshopify.com" />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: !editingId, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter your API key" />
          </Form.Item>

          <Form.Item
            name="api_secret"
            label="API Secret (Optional)"
          >
            <Input.Password placeholder="Enter your API secret" />
          </Form.Item>
        </Form>
      </Modal>

      <UpgradeModal
        visible={upgradeModalVisible}
        onClose={() => setUpgradeModalVisible(false)}
        limitType="api_access"
        currentPlan={currentPlan?.plan_name}
      />
    </div>
  );
};

export default APIKeys;
